import { AppState } from "../types";
import { ITelemetryClient } from "./contracts";
import { areDiagnosticsEnabled } from "./privacyPreferences";

export const TELEMETRY_STORAGE_KEY = "monyawn-ui-telemetry-v1";

export type TelemetryEvent = {
  at: string;
  action: string;
  mode: AppState["currentMode"];
  selectedOpportunityId: string;
  delta: {
    proofDelta: number;
    tasksDelta: number;
    dmsDelta: number;
    storiesDelta: number;
  };
};

export type FrictionSnapshot = {
  totalActions: number;
  repetitiveActions: number;
  manualHeavyActions: number;
  lastActionAt: string;
};

class LocalTelemetryClient implements ITelemetryClient {
  track(event: string, properties?: any) {
    const next = [
      ...this.readEvents(),
      { at: new Date().toISOString(), action: event, ...properties },
    ];
    this.writeEvents(next);
  }

  getSnapshot(): FrictionSnapshot {
    return getFrictionSnapshot();
  }

  private readEvents(): any[] {
    if (typeof window === "undefined") return [];
    const raw = window.localStorage.getItem(TELEMETRY_STORAGE_KEY);
    try {
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  private writeEvents(events: any[]) {
    if (typeof window === "undefined") return;
    if (!areDiagnosticsEnabled()) {
      return;
    }
    window.localStorage.setItem(TELEMETRY_STORAGE_KEY, JSON.stringify(events.slice(-250)));
  }
}

export const telemetryClient: ITelemetryClient = new LocalTelemetryClient();

function readEvents(): TelemetryEvent[] {
  if (typeof window === "undefined") {
    return [];
  }

  if (!areDiagnosticsEnabled()) {
    return [];
  }

  const raw = window.localStorage.getItem(TELEMETRY_STORAGE_KEY);
  if (!raw) {
    return [];
  }

  try {
    return JSON.parse(raw) as TelemetryEvent[];
  } catch {
    return [];
  }
}

function writeEvents(events: TelemetryEvent[]) {
  if (typeof window === "undefined") {
    return;
  }

  if (!areDiagnosticsEnabled()) {
    return;
  }

  window.localStorage.setItem(TELEMETRY_STORAGE_KEY, JSON.stringify(events.slice(-250)));
}

export function trackUiAction(event: TelemetryEvent) {
  if (!areDiagnosticsEnabled()) {
    return;
  }

  const next = [...readEvents(), event];
  writeEvents(next);
}

export function clearTelemetryEvents() {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(TELEMETRY_STORAGE_KEY);
}

export function getFrictionSnapshot(): FrictionSnapshot {
  const events = readEvents();
  if (events.length === 0) {
    return {
      totalActions: 0,
      repetitiveActions: 0,
      manualHeavyActions: 0,
      lastActionAt: "",
    };
  }

  const repetitiveActions = events.reduce((count, current, index, source) => {
    if (index === 0) {
      return count;
    }

    return source[index - 1].action === current.action ? count + 1 : count;
  }, 0);

  const manualHeavyActions = events.filter((event) => {
    const lowered = event.action.toLowerCase();
    return (
      lowered.includes("edit") ||
      lowered.includes("confirm") ||
      lowered.includes("import") ||
      lowered.includes("export")
    );
  }).length;

  return {
    totalActions: events.length,
    repetitiveActions,
    manualHeavyActions,
    lastActionAt: events[events.length - 1]?.at ?? "",
  };
}

export function summarizeStateDelta(current: AppState, next: AppState) {
  return {
    proofDelta: next.proof.length - current.proof.length,
    tasksDelta: next.tasks.length - current.tasks.length,
    dmsDelta: next.dms.length - current.dms.length,
    storiesDelta: next.candidateStories.length - current.candidateStories.length,
  };
}
